import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const EmailDetection = ({ email, onEmailExists, onEmailNew, onCancel, canCancel = true, requireRegistration = false }) => {
  const [checking, setChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (email && email.includes('@')) {
      checkEmailExists(email);
    }
  }, [email]);

  const checkEmailExists = async (emailToCheck) => {
    setChecking(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/auth/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailToCheck })
      });

      const data = await response.json();
      setEmailExists(data.exists);
      
      if (data.exists) {
        onEmailExists(emailToCheck);
      } else {
        onEmailNew(emailToCheck);
      }
    } catch (error) {
      console.error('Error checking email:', error);
      // If check fails, assume new email
      onEmailNew(emailToCheck);
    } finally {
      setChecking(false);
    }
  };

  const handleLogin = () => {
    const currentUrl = window.location.href;
    const redirectUrl = encodeURIComponent(currentUrl);
    try {
      // Lazy import to avoid circular import issues in some bundlers
      const { getCustomerBaseUrl } = require('../utils/customerBase');
      window.location.href = `${getCustomerBaseUrl()}/login?redirect=${redirectUrl}&email=${encodeURIComponent(email)}`;
    } catch (e) {
      window.location.href = `/login?redirect=${redirectUrl}&email=${encodeURIComponent(email)}`;
    }
  };

  const handleRegister = () => {
    const currentUrl = window.location.href;
    const redirectUrl = encodeURIComponent(currentUrl);
    navigate(`/customer/register?redirect=${redirectUrl}&email=${encodeURIComponent(email)}`)
  };

  if (checking) {
    return (
      <div className="email-detection-loading">
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Checking email...</span>
        </div>
      </div>
    );
  }

  if (emailExists === null) {
    return null;
  }

  return (
    <div className="email-detection-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {emailExists ? 'Account Found' : 'New Account'}
          </h3>
          
          <p className="text-sm text-gray-500 mb-6">
            {emailExists
              ? `We found an account with ${email}. ${requireRegistration ? 'Please sign in to continue.' : 'Would you like to sign in?'}`
              : `No account found with ${email}. ${requireRegistration ? 'Please create an account to continue.' : 'Would you like to create a new account?'}`
            }
          </p>

          {requireRegistration && !emailExists && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                ℹ️ This store requires account registration for all orders.
              </p>
            </div>
          )}

          <div className="flex space-x-3">
            {canCancel && (
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            )}

            {emailExists ? (
              <button
                onClick={handleLogin}
                className={`${canCancel ? 'flex-1' : 'w-full'} px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                Sign In
              </button>
            ) : (
              <button
                onClick={handleRegister}
                className={`${canCancel ? 'flex-1' : 'w-full'} px-4 py-2 bg-green-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
              >
                Create Account
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDetection;

